import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Send, User, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { NavBar, Footer } from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) { toast.error("Remplis tous les champs."); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/contact`, { name, email, message });
      toast.success(r.data.message);
      setName(""); setEmail(""); setMessage("");
    } catch {
      toast.error("Erreur lors de l'envoi. Réessaie.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <NavBar active="contact" />

      {/* Hero */}
      <section className="relative py-16 sm:py-20 overflow-hidden gradient-hero grid-overlay">
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[80px] -translate-y-1/2 -translate-x-1/4"></div>
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Contactez-<span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">nous</span>
            </h1>
            <p className="text-lg text-blue-200/70 max-w-2xl mx-auto">
              Une question sur nos guides de révision, le tutorat en mathématiques ou le soutien scolaire? N'hésite pas à nous écrire, nous te répondrons rapidement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 sm:py-20 bg-navy-800">
        <div className="max-w-2xl mx-auto px-4">
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="card-dark glow-blue">
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium text-blue-200/70 mb-2">Nom</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-300/40" />
                  <input type="text" data-testid="contact-name" value={name} onChange={e => setName(e.target.value)}
                    placeholder="Ton nom" required
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-white placeholder-blue-300/30" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-200/70 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-300/40" />
                  <input type="email" data-testid="contact-email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="ton.email@example.com" required
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-white placeholder-blue-300/30" />
                </div>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-blue-200/70 mb-2">Message</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-blue-300/40" />
                <textarea data-testid="contact-message" value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Écris ton message ici..." required rows={5}
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all resize-none text-white placeholder-blue-300/30" />
              </div>
            </div>
            <button type="submit" data-testid="contact-submit" disabled={loading} className="btn-gold flex items-center gap-2">
              {loading ? <div className="spinner"></div> : <><Send className="w-5 h-5" /> Envoyer le message</>}
            </button>
          </motion.form>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-8 text-center">
            <p className="text-blue-200/50 text-sm">Ou écris-nous directement à</p>
            <a href="mailto:excelltutorat@outlook.com" className="text-amber-400 font-medium hover:text-amber-300 transition-colors">
              excelltutorat@outlook.com
            </a>
          </motion.div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12 bg-navy-900 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-white font-semibold mb-4">Nos ressources pédagogiques pour réussir en mathématiques</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/sn4" className="text-amber-400 hover:text-amber-300 text-sm font-medium border border-amber-500/20 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-all">Cahier SN4</Link>
            <Link to="/cst4" className="text-amber-400 hover:text-amber-300 text-sm font-medium border border-amber-500/20 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-all">Cahier CST4</Link>
            <Link to="/gratuit" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium border border-emerald-500/20 px-4 py-2 rounded-lg hover:bg-emerald-500/10 transition-all">Aide-mémoire gratuits</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
